
import {
  defineComponent,
  ref,
  reactive,
  toRefs,
  onMounted,
  computed,
  watch,
  nextTick
} from 'vue'
import dataLoad from './dataLoad.vue'
import infoPanel from './infoPanel.vue'
import Charts from '@/components/page/charts.vue'
import f10Api from '@/api/f10Api'
import { useRoute } from 'vue-router'

export default defineComponent({
  components: { dataLoad, infoPanel, Charts },
  props: {
    token: {
      type: String,
      default: function () {
        return ''
      }
    }
  },
  setup(props, context) {
    const pageData: any = reactive({
      list: [
        {
          key: 'PE_TTM',
          title: '市盈率（ttm）',
          content: '',
          format: 'type1'
        },
        {
          key: 'PE_TTM_PR',
          title: '市盈率（ttm）百分位',
          content: '',
          format: 'type3'
        },
        {
          key: 'PB',
          title: '市净率',
          content: '',
          format: 'type1'
        },
        {
          key: 'PB_PR',
          title: '市净率百分位',
          content: '',
          format: 'type3'
        },
        {
          key: 'PS_TTM',
          title: '市销率（ttm）',
          content: '',
          format: 'type1'
        },
        {
          key: 'PS_TTM_PR',
          title: '市销率（ttm）百分位',
          content: '',
          format: 'type2'
        },
        {
          key: 'PEG',
          title: 'PEG',
          content: '',
          format: 'type1'
        },
        {
          key: 'PEG_PR',
          title: 'PEG百分位',
          content: '',
          format: 'type2'
        }
      ],
      spinning: true,
      activeKey: '5',
      chartForm: {
        valuation: 'PE_TTM',
        superposition: ''
      },
      valuationList: [
        {
          label: '市盈率（ttm）',
          value: 'PE_TTM'
        },
        {
          label: '市净率',
          value: 'PB'
        },
        {
          label: '市销率（ttm）',
          value: 'PS_TTM'
        },
        {
          label: 'PEG',
          value: 'PEG'
        }
      ],
      superpositionList: [
        {
          label: '不叠加',
          value: ''
        },
        {
          label: '上证指数',
          value: '000001',
          inst: 'sh'
        },
        {
          label: '深证成指',
          value: '399001',
          inst: 'sz'
        },
        {
          label: '沪深300',
          value: '000300',
          inst: 'sh'
        },
        {
          label: '创业板指',
          value: '399006',
          inst: 'sz'
        },
        {
          label: '中证500',
          value: '000905',
          inst: 'sh'
        }
      ],
      option: {},
      chartsData: [],
      abbr: ''
    })
    const route = useRoute()
    watch(
      () => (props as any).token,
      (val) => {
        if (val) {
          getBaseIndexF10Overview().then(() => {
            getBaseIndexF10ValuationTrend()
          })
        }
      },
      { deep: true, immediate: true }
    )

    const superpositionListRow = computed(() => {
      const code = route.query.code
      return pageData.superpositionList.filter((v) => v.value !== code)
    })

    // 获取最新指数信息
    function getBaseIndexF10Overview(): Promise<void> {
      return new Promise((resolve, reject) => {
        const data: any = {}
        data.data = {
          Exch: route.query.market,
          Inst: route.query.code
        }
        data.headers = {
          token: (props as any).token
        }
        f10Api.baseIndexF10Overview(data).then((res: any) => {
          if (res.Code === 0) {
            pageData.abbr = res.Data.Abbr
          }
          resolve()
        })
      })
    }

    // 获取估值信息
    function getBaseIndexF10ValuationTrend() {
      const data: any = {}
      data.data = {
        Exch: route.query.market,
        Inst: route.query.code,
        IndexYear: Number(pageData.activeKey)
      }
      data.headers = {
        token: (props as any).token
      }
      if (pageData.chartForm.superposition !== '') {
        const item = pageData.superpositionList.find(
          (v) => v.value === pageData.chartForm.superposition
        )
        data.data.CompareExch = item.inst
        data.data.CompareInst = item.value
      }

      f10Api
        .baseIndexF10ValuationTrend(data)
        .then((res: any) => {
          if (res.Code === 0) {
            const formatMap = {
              type1: (val) => val.toFixed(2),
              type2: (val) => val.toFixed(2) + '%',
              type3: (val) => parseInt(val) + '%'
            }
            pageData.list.forEach((item) => {
              item.content = formatMap[item.format](res.Data.Latest[item.key])
            })

            // 图表
            pageData.chartsData = res.Data
            setChart()
          }
          pageData.spinning = false
        })
    }

    // 图表动态初始化
    function setChart() {
      const { List, Compare, Quantile } = pageData.chartsData
      // 不叠加
      let series: Array<any> = []
      let legend: Array<any> = []
      if (pageData.chartForm.superposition === '') {
        const QuantileItem = Quantile.find(
          (v) => v.IndicatorField === pageData.chartForm.valuation
        )
        series = [
          {
            name: `30分位值 ${QuantileItem.Low.toFixed(2)}`,
            type: 'line',
            data: List.map((v) => {
              return QuantileItem.Low
            }),
            smooth: false,
            itemStyle: {
              normal: {
                lineStyle: {
                  width: 2,
                  type: 'dashed'
                }
              }
            },
            symbol: 'none'
          },
          {
            name: `50分位值 ${QuantileItem.Middle.toFixed(2)}`,
            type: 'line',
            data: List.map((v) => {
              return QuantileItem.Middle
            }),
            smooth: false,
            itemStyle: {
              normal: {
                lineStyle: {
                  width: 2,
                  type: 'dashed'
                }
              }
            },
            symbol: 'none'
          },
          {
            name: `70分位值 ${QuantileItem.High.toFixed(2)}`,
            type: 'line',
            data: List.map((v) => {
              return QuantileItem.High
            }),
            smooth: false,
            itemStyle: {
              normal: {
                lineStyle: {
                  width: 2,
                  type: 'dashed'
                }
              }
            },
            symbol: 'none'
          },
          {
            name: pageData.abbr,
            type: 'line',
            data: (List as any).map((v) => v[pageData.chartForm.valuation]),
            symbol: 'none'
          }
        ]

        legend = [
          {
            bottom: 10,
            itemWidth: 15,
            left: '350',
            itemHeight: 8,
            data: [
              {
                name: `30分位值 ${QuantileItem.Low.toFixed(2)}`,
                textStyle: { color: '#7D7D7D' }
              }
            ]
          },
          {
            bottom: 10,
            itemWidth: 15,
            left: '500',
            itemHeight: 8,
            data: [
              {
                name: `50分位值 ${QuantileItem.Middle.toFixed(2)}`,
                textStyle: { color: '#7D7D7D' }
              }
            ]
          },
          {
            bottom: 10,
            itemWidth: 15,
            left: '650',
            itemHeight: 8,
            data: [
              {
                name: `70分位值 ${QuantileItem.High.toFixed(2)}`,
                textStyle: { color: '#7D7D7D' }
              }
            ]
          }
        ]
      } else {
        const superpositionItem = pageData.superpositionList.find(
          (v) => v.value === pageData.chartForm.superposition
        )
        series = [
          {
            name: pageData.abbr,
            type: 'line',
            data: (List as any).map((v) => v[pageData.chartForm.valuation]),
            symbol: 'none'
          },
          {
            name: superpositionItem.label,
            type: 'line',
            data: (Compare as any).map((v) => v[pageData.chartForm.valuation]),
            symbol: 'none'
          }
        ]
      }

      pageData.option = {
        color:
          pageData.chartForm.superposition === ''
            ? ['#12B362', '#B5B8BD', '#FF4D4D', '#387AFF', '#ea5504']
            : ['#257ce6', '#ea5504'],
        tooltip: {
          trigger: 'axis',
          formatter: function (params: any) {
            var htmlStr = ''
            let data = params
            if (pageData.chartForm.superposition === '') {
              data = params.slice(3)
            }

            htmlStr +=
              '<div class="optiontooltip"><p style="color:rgba(0,0,0,0.45);">' +
              params[0].name +
              '</p>'
            for (var i = 0; i < data.length; i++) {
              // 前面的原点和他的颜色
              let style = 'height: 8px;'
              if (i === 1) {
                style = 'height: 2px;'
              }
              htmlStr +=
                '<span style="width: 8px;' +
                style +
                'vertical-align: middle;display:inline-block;margn-right: 4px;background-color:' +
                data[i].color +
                '"></span><span style="color:#000;margin-left: 6px;">' +
                data[i].seriesName +
                ':</span>' +
                '<span style="color:#000;float:right;margin-left: 12px">' +
                data[i].value +
                '</span><br/>'
            }
            htmlStr += '</div>'
            return htmlStr
          }
        },
        grid: [
          { top: 40, left: 30, right: 30, bottom: 50, containLabel: true }
        ],
        toolbox: { feature: {} },
        legend,
        xAxis: [
          {
            type: 'category',
            data: List.map((v) =>
              window.moment(v.DateTime * 1000).format('YYYY-MM-DD')
            ),
            axisPointer: { type: 'shadow' },
            axisLine: { lineStyle: { color: '#7D7D7D' } },
            axisTick: { show: false }
          }
        ],
        yAxis: [
          {
            name:
              pageData.chartForm.superposition === ''
                ? '不叠加走势图'
                : '叠加走势图',
            type: 'value',
            splitLine: { lineStyle: { color: '#252A34' } },
            axisLine: { lineStyle: { color: '#7D7D7D' } },
            axisLabel: {
              formatter: {
                _custom: {
                  type: 'function',
                  display:
                    '<span style="opacity:.5;">function</span> (value, index)',
                  tooltip:
                    "<pre>function (value, index) {\r\n                                if (i === 0 && max) {\r\n                                    if (max) {\r\n                                        if (Math.abs(max) >= 100000000 && value !== 0) {\r\n                                            return (value / 100000000).toFixed(2) + '亿';\r\n                                        }\r\n                                        else if (Math.abs(max) >= 10000 && value !== 0) {\r\n                                            return (value / 10000).toFixed(2) + '万';\r\n                                        }\r\n                                        else {\r\n                                            return value;\r\n                                        }\r\n                                    }\r\n                                }\r\n                                else {\r\n                                    if (Math.abs(value) >= 100000000) {\r\n                                        return (value / 100000000).toFixed(2) + '亿';\r\n                                    }\r\n                                    else if (Math.abs(value) >= 10000) {\r\n                                        return (value / 10000).toFixed(2) + '万';\r\n                                    }\r\n                                    else {\r\n                                        return value;\r\n                                    }\r\n                                }\r\n                            }</pre>",
                  _reviveId: 859
                }
              }
            }
          }
        ],
        series
      }
    }

    return {
      ...toRefs(pageData),
      superpositionListRow,
      setChart,
      getBaseIndexF10ValuationTrend
    }
  }
})
